import React, {useState, useCallback} from 'react';
import {invalidatePartialInterest, genericMandarkRequest} from 'resources/mandark.resource';
import {
  addToast,
  Button,
  Dialogue,
  Dropdown,
  Icon,
  IconButton,
  MenuListItem,
  Modal,
  copyToClipboard,
  Text
} from '@albert-io/atomic';

import ConfirmActionModal from 'client/components/ConfirmActionModal/ConfirmActionModal.react';
import CustomizeReassignModal from 'client/components/CustomizeReassignModal/CustomizeReassignModal.react';
import moment from 'moment';
import type {AssignmentModelV3} from '@albert-io/models';
import {AddAssignmentQuestionSetsToFolderModal} from 'client/Classrooms/Classroom/Teacher/Assignments/AssignmentsList/AssignmentRowOptions.react';
import constants from 'client/constants';
import {useArchivedQuestionModal} from 'client/components/ArchivedQuestions/useArchivedQuestionModal';
import ArchivedQuestionModal from 'client/components/ArchivedQuestions/ArchivedQuestionModal.react';

interface AssignmentMenuOptionsProps {
  canEdit: boolean;
  router: any;
  assignment: AssignmentModelV3;
  location: string;
  onDeleteComplete?: () => void;
}

const MODAL_TYPE = {
  DETAILS: 'details',
  DELETE: 'delete',
  REASSIGN: 'reassign',
  FOLDER: 'folder',
  ARCHIVED_QUESTIONS: 'archived_questions',
  NONE: ''
} as const;

type ModalType = (typeof MODAL_TYPE)[keyof typeof MODAL_TYPE];

const DROPDOWN_CONFIG = {
  position: 'bottom-end',
  trayProps: {
    modifiers: {
      flip: {
        enabled: false
      }
    }
  }
} as const;

const AssignmentMenuOptions = ({
  canEdit,
  router,
  assignment,
  location,
  onDeleteComplete
}: AssignmentMenuOptionsProps) => {
  const [modalShown, setModalShown] = useState<ModalType>(MODAL_TYPE.NONE);
  const isDraft = assignment.status === 'draft';
  const assignmentId = assignment.id;
  const isNewAssignment = !assignmentId;

  const showEditMenu = location === 'assignment-list' || (location === 'navigation' && !isDraft);

  const {showArchivedQuestionModal, modalProps, getArchivedQuestionCount} =
    useArchivedQuestionModal({
      questionSets: assignment.getQuestionSets(),
      type: 'save-to-folder',
      serverArchivedQuestionCount: assignment?.meta?.get?.('count_of_archived_questions')
    });

  const handleModalMenuClick = (modalType: ModalType) => (e: React.MouseEvent) => {
    if (modalType === MODAL_TYPE.DELETE && !canEdit) return; // prevent delete if you do not have permission to edit
    e.stopPropagation();
    setModalShown(modalType);
  };

  const handleModalKeyDown = (modalType: ModalType) => (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      setModalShown(modalType);
    }
  };

  const handleEditClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    const path = `/assignment-builder/create-assignment/${assignment.id}/settings${
      isDraft ? '/draft' : ''
    }`;
    router.push(path);
  };

  const handleCopyLinkClick =
    (linkType: 'student' | 'educator') => (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      copyShareLink(linkType);
    };

  // Handle "Save Questions to Folder" with archived question check
  const handleFolderClick = useCallback(
    async (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();

      try {
        const userConfirmed = await showArchivedQuestionModal();

        if (!userConfirmed) {
          return;
        }

        setModalShown(MODAL_TYPE.FOLDER);
      } catch {
        // Fallback: show folder modal anyway
        setModalShown(MODAL_TYPE.FOLDER);
      }
    },
    [showArchivedQuestionModal]
  );

  const handleDeleteAssignment = async () => {
    const deleteQuery = {
      resourcePath: [isDraft ? 'draft_assignments_v1' : 'assignments_v3', assignmentId]
    };
    await genericMandarkRequest('delete', deleteQuery);
  };

  const handleDeleteSuccess = () => {
    invalidatePartialInterest({
      resourcePath: [isDraft ? 'draft_assignments_v1' : 'assignments_v3']
    });
    setModalShown(MODAL_TYPE.NONE);
    if (onDeleteComplete) {
      onDeleteComplete();
    } else {
      router.push('/classes');
    }
  };

  const copyShareLink = (type: 'student' | 'educator') => {
    const shareUrl =
      type === 'student'
        ? `${constants.ALBERT_FULL_PATH}/assignment/${assignmentId}`
        : `${constants.ALBERT_FULL_PATH}/assignment-builder/create-assignment/${assignmentId}/settings`;

    copyToClipboard(shareUrl, () => {
      addToast({
        color: 'positive',
        title: 'Link Copied!',
        message: `The ${type} link to this assignment has been copied to your clipboard.`
      });
    });
  };

  const createDivider = () => <div className='m-x1 border-b border-b-border-primary' />;

  const renderDetailsMenuItem = () => (
    <>
      <MenuListItem
        className='global-assignments__card-dropdown-item'
        disabled={isNewAssignment}
        onMenuItemClick={handleModalMenuClick(MODAL_TYPE.DETAILS)}
      >
        <Icon icon='memo-circle-info' iconStyle='regular' className='fa-fw mr-x1' />
        Document Details
      </MenuListItem>
      {isDraft && location === 'assignment-list' && createDivider()}
    </>
  );

  const renderEditMenuItem = () => {
    if (!showEditMenu) return null;

    return (
      <MenuListItem
        onClick={handleEditClick}
        onKeyDown={handleEditClick}
        className='global-assignments__card-dropdown-item'
      >
        <Icon icon={isDraft ? 'pencil' : 'gear'} iconStyle='regular' className='fa-fw mr-x1' />
        Edit {isDraft ? 'Draft' : 'Recipient Settings'}
      </MenuListItem>
    );
  };

  const renderReassignMenuItem = () => {
    if (isDraft) return null;

    return (
      <>
        {createDivider()}
        <MenuListItem
          onClick={handleModalMenuClick(MODAL_TYPE.REASSIGN)}
          onKeyDown={handleModalKeyDown(MODAL_TYPE.REASSIGN)}
          className='global-assignments__card-dropdown-item'
        >
          <Icon icon='rotate-right' iconStyle='regular' className='fa-fw mr-x1' />
          Duplicate & Reassign
        </MenuListItem>
      </>
    );
  };

  const renderFolderMenuItem = () => (
    <MenuListItem
      onClick={handleFolderClick}
      onKeyDown={handleFolderClick}
      className='global-assignments__card-dropdown-item'
    >
      <Icon icon='folder' iconStyle='regular' className='fa-fw mr-x1' />
      Save Questions to Folder
    </MenuListItem>
  );

  const renderShareLinkMenuItems = () => {
    if (isDraft) return null;

    return (
      <>
        {createDivider()}
        <MenuListItem
          onClick={handleCopyLinkClick('student')}
          onKeyDown={handleCopyLinkClick('student')}
          className='global-assignments__card-dropdown-item'
        >
          <Icon icon='copy' iconStyle='regular' className='fa-fw mr-x1' />
          Copy Student Share Link
        </MenuListItem>
        <MenuListItem
          onClick={handleCopyLinkClick('educator')}
          onKeyDown={handleCopyLinkClick('educator')}
          className='global-assignments__card-dropdown-item'
        >
          <Icon icon='copy' iconStyle='regular' className='fa-fw mr-x1' />
          Copy Educator Share Link
        </MenuListItem>
      </>
    );
  };

  const renderDeleteMenuItem = () => (
    <>
      {createDivider()}
      <MenuListItem
        onClick={handleModalMenuClick(MODAL_TYPE.DELETE)}
        onKeyDown={handleModalKeyDown(MODAL_TYPE.DELETE)}
        className='global-assignments__card-dropdown-item flex flex-col'
        data-testid={`delete-${assignment.id}`}
        disabled={!canEdit}
      >
        <div className='flex gap-x1'>
          <Icon
            className='fa-fw pt-x0-half'
            icon='trash-can'
            iconStyle='regular'
            color='negative'
          />
          <div className='flex flex-col'>
            <Text color='negative'>Delete</Text>
            {!canEdit && (
              <Text className='assignment-navigation-dropdown_tex w-[168px]' size='xs'>
                You don&apos;t have permission to delete this assignment.
              </Text>
            )}
          </div>
        </div>
      </MenuListItem>
    </>
  );

  const showModals = () => {
    switch (modalShown) {
      case MODAL_TYPE.DETAILS:
        return (
          <Modal handleClose={() => setModalShown(MODAL_TYPE.NONE)} ariaLabel='Document Details'>
            {() => (
              <Dialogue
                inModal
                handleClose={() => setModalShown(MODAL_TYPE.NONE)}
                size='s'
                className='document-details'
              >
                <Text size='m' bold>
                  Document Details
                </Text>
                <div className='mt-x3 mb-x2 flex items-center gap-x1'>
                  <Icon icon='user' iconStyle='regular' />
                  Created by: {assignment.getTeacher().getFullName()}
                </div>
                <div className='flex items-center gap-x1'>
                  <Icon icon='calendar' iconStyle='regular' />
                  Last modified {moment(assignment.updated_at).format('MMM D, YYYY')}
                </div>
              </Dialogue>
            )}
          </Modal>
        );
      case MODAL_TYPE.DELETE:
        return (
          <ConfirmActionModal
            title='Are you sure?'
            align='center'
            handleClose={() => setModalShown(MODAL_TYPE.NONE)}
            makeRequest={handleDeleteAssignment}
            onResolve={handleDeleteSuccess}
          >
            {({isRequestInflight, onCancel, onConfirm}) => (
              <>
                <Dialogue.Body align='center'>
                  {isDraft
                    ? 'Are you sure you want to delete this assignment?'
                    : 'This will delete the assignment for all classes and students. This action cannot be undone.'}
                </Dialogue.Body>
                <Dialogue.BtnGroup align='center'>
                  <Button color='secondary' disabled={isRequestInflight} onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button color='negative' disabled={isRequestInflight} onClick={onConfirm}>
                    Delete
                  </Button>
                </Dialogue.BtnGroup>
              </>
            )}
          </ConfirmActionModal>
        );
      case MODAL_TYPE.REASSIGN:
        return (
          <CustomizeReassignModal
            assignmentId={assignment.id}
            assignmentName={assignment.name}
            handleClose={() => setModalShown(MODAL_TYPE.NONE)}
          />
        );
      case MODAL_TYPE.FOLDER:
        return (
          <AddAssignmentQuestionSetsToFolderModal
            assignmentId={assignment.id}
            handleClose={() => setModalShown(MODAL_TYPE.NONE)}
            questionCount={assignment.getCountOfQuestions()}
            isDraft={isDraft}
            archivedQuestionCount={getArchivedQuestionCount()}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dropdown trigger={<IconButton icon='ellipsis-v' label='menu' />} {...DROPDOWN_CONFIG}>
        <div className='min-w-[245px] p-x1'>
          {renderDetailsMenuItem()}
          {renderEditMenuItem()}
          {renderReassignMenuItem()}
          {renderFolderMenuItem()}
          {renderShareLinkMenuItems()}
          {renderDeleteMenuItem()}
        </div>
      </Dropdown>

      {/* Archived Questions Modal */}
      <ArchivedQuestionModal {...modalProps} />

      {/* Other Modals */}
      {showModals()}
    </>
  );
};

export default AssignmentMenuOptions;
